import React, { Component, useState } from "react";
import clsx from "clsx";
import _ from "lodash";
import { connect } from "react-redux";
import DashboardApi from "../../api/DashboardApi";
import "./Translation.scss";
import Alert from '../../Components/Alert';

export class Translation extends Component {
  constructor() {
    super();

    this.state = {
      loading: true,
      originalTranslations: {},
      translations: {},
      accessors: [],
      locales: [],
      errors: [],
      differences: [],
      lang: undefined
    };
  }

  componentDidMount() {
    this.getTranslations();
  }

  /**
   * 
   * @param {*} content - Object to get keys of
   * @param {*} parentKey - Parent key to keep track of current reference
   * @param {*} key - current key in the loop
   * @param {*} keysObj - the resulting object of arrays
   * 
   * @description populate array with object keys in dot format (easier to use with lodash)
   *  this modifies original array
   */
  contentify(content, parentKey, key, keysObj) {
    const clonedContent = _.get(content, key);
    if (typeof clonedContent === "string") {
      keysObj[parentKey].push(`${parentKey}.${key}`);
    } else {
      Object.keys(clonedContent).forEach(k => {
        if (typeof clonedContent[k] === "string") {
          keysObj[parentKey].push(`${parentKey}.${key}.${k}`);
        } else {
          this.contentify(content, parentKey, `${key}.${k}`, keysObj);
        }
      });
    }
  }

  getTranslations = async () => {
    try {
      const result = await DashboardApi.getTranslations();
      if (result.success === true) {
        const translations = result.data;
        const originalTranslations = _.cloneDeep(translations);
        // get locale accessors
        let locales = Object.keys(translations);
        const lang = locales[0];
        
        locales = locales.map(lang =>
          lang.toUpperCase()
        );
        // get sections per default locale
        // const sections = Object.keys(translations.ar).map(sect => sect);

        const accessors = {};
        Object.keys(translations.ar).forEach(sect => {
          accessors[sect] = [];
          Object.keys(translations.ar[sect]).forEach(c =>
            this.contentify(translations.ar[sect], sect, c, accessors)
          );
        });

        this.setState({
          translations,
          originalTranslations,
          locales,
          lang,
          accessors,
          loading: false
        });
      } else {
        this.dispatchError(result.data.errors);
      }
    } catch (error) {
      this.dispatchError(error);
    }
  };

  setTranslations = async () => {
    if (!this.state.differences.length) {
      return alert('No differences detected!');
    }

    this.setState({ loading: true });

    const result = await DashboardApi.updateTranslations(this.state.translations);
    if (result.success) {
      // reset differences as they're not differences anymore
      const differences = [];
      // update original translations to match current saved translations
      const originalTranslations = _.cloneDeep(this.state.translations);
      this.setState({ differences, originalTranslations, loading: false });
    } else {
      this.setState({ errors: result.data.errors, loading: false });
    }
  }

  switchLang = lang => {
    this.setState({ lang: lang.toLowerCase() });
  };

  handleFieldChange = (key, e) => {
    const value = e.target.value;
    const lang = this.state.lang.toLowerCase();
    const translations = this.state.translations;
    const differences = new Set(this.state.differences);
    const originalTranslations = this.state.originalTranslations;
    const originalValue = _.get(originalTranslations[lang], key);

    _.set(translations[lang], key, value);

    if (originalValue !== value) {
      differences.add(`${lang}.${key}`);
    } else {
      differences.delete(`${lang}.${key}`);
    }
    
    this.setState({ translations, differences: Array.from(differences) });
  }

  dispatchError = errors => {
    console.error(errors);
    if (typeof errors === "string") {
      errors = [errors];
    } else
    if (Array.isArray(errors)) {
      // do nothing...
    } else {
      errors = [JSON.stringify(errors)];
    }

    this.setState({ errors, loading: false });
    setTimeout(() => {
      this.setState({ errors: [] });
    }, 4000);
  };

  render() {
    const { differences } = this.state;

    const saveBtnClasses = ['save-btn'];

    if (differences.length) {
      saveBtnClasses.push('active');
    }

    return (
      <div className="container">
        {this.state.loading && "Loading..."}
        {this.state.errors.length > 0 && <Alert color="red" msg={this.state.errors} />}
        <div className="actions">
          {
            <>
              <div className="locales">
                {this.state.locales.map(lang => {
                  const current = [];

                  if (this.state.lang.toUpperCase() === lang) {
                    current.push('active');
                  }
                  return (
                    <>
                      <div className="rb">
                        <button className={clsx(current)} onClick={this.switchLang.bind(this, lang)}>{lang}</button>
                      </div>
                    </>
                  )
                })}
              </div>
              <div className="rb">
                <button className={clsx(saveBtnClasses)} onClick={this.setTranslations}>Save</button>
              </div>
            </>
          }
        </div>
        {Object.keys(this.state.accessors).map(key => (
          <TranslationSection
            title={key.replace(/[_]/g, ' ').replace(/^\w|\b\w/g, (l) => l.toUpperCase())}
            content={this.state.accessors[key]}
            translations={this.state.translations}
            differences={this.state.differences}
            lang={this.state.lang}
            handleFieldChange={this.handleFieldChange}
          />
        ))}
      </div>
    );
  }
}

function TranslationSection({ title, content, translations, differences, handleFieldChange, lang }) {
  const [open, setOpen] = useState(false);
  let hasMissingValue = false;
  let hasNewValue = false;
  const contentClasses = ["section_content"];

  const contentJsx = [];

  if (open) {
    contentClasses.push("open");
  }

  content.forEach(key => {
    const rowClasses = [];
    const value = _.get(translations[lang], key);
    const fmtKey = key.split(".");
    fmtKey.splice(0, 1);

    if (differences.includes(`${lang.toLowerCase()}.${key}`)) {
      hasNewValue = true;
      rowClasses.push('new');
    }

    if (!value) {
      hasMissingValue = true;
      rowClasses.push('missing');
    }

    contentJsx.push(
      <tr className={clsx(rowClasses)}>
        <td>{fmtKey.join(".")}</td>
        <td>
          <input type="text" className="field" onChange={handleFieldChange && handleFieldChange.bind(this, key)} value={value}/>
          { lang !== 'ar' && <p>{_.get(translations.ar, key)}</p>}
        </td>
      </tr>
    );
  });

  const dirClass = lang === 'ar'? 'rtl': '';

  const sectTitleClasses = ['section_title'];

  if (hasMissingValue) {
    sectTitleClasses.push('missing');
  } else
  if (hasNewValue) {
    sectTitleClasses.push('new');
  }

  return (
    <section className={dirClass}>
      <div className={clsx(sectTitleClasses)} onClick={() => setOpen(!open)}>
        {title}
      </div>
      <div className={clsx(contentClasses)}>
        <table className="basic-table align-left">
          {contentJsx}
        </table>
      </div>
    </section>
  );
}

const mapStateToProps = state => ({});

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Translation);
